import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, RulerDimensionLine, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StickerRenderer, labelDimensions } from "@/components/StickerRenderer";
import { generateBarcodeDataUrl } from "@/lib/barcode";

// A4 sheet: 210mm x 297mm ≈ 8.27in x 11.69in
const A4_IN = { w: 8.27, h: 11.69 };
const DEFAULT_MARGIN = 0.2;
const MAX_QTY = 5000;

export default function PrintPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [width, setWidth] = useState(2.5);
  const [height, setHeight] = useState(2.5);
  const [qty, setQty] = useState(20);
  const [margin, setMargin] = useState(DEFAULT_MARGIN);
  const [gap, setGap] = useState(0);
  const [rotate, setRotate] = useState(true); // Roll printers feed portrait — rotate design 90° by default
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setProduct(data);
        const dims = labelDimensions(data.label_type);
        setWidth(dims.widthIn);
        setHeight(dims.heightIn);
      } catch {
        toast.error("Product not found");
        navigate("/");
      }
    })();
  }, [id, navigate]);

  const isRoll = product && (product.label_type === "india" || product.label_type === "china");

  // Pre-render barcode ONCE as a data URL — reused across every sticker.
  const barcodeDataUrl = useMemo(
    () => (product ? generateBarcodeDataUrl(product.ean_code) : null),
    [product]
  );

  const { perRow, perCol, perSheet, sheets } = useMemo(() => {
    const usableW = A4_IN.w - margin * 2;
    const usableH = A4_IN.h - margin * 2;
    const cellW = width + gap;
    const cellH = height + gap;
    const pr = Math.max(1, Math.floor((usableW + gap) / cellW));
    const pc = Math.max(1, Math.floor((usableH + gap) / cellH));
    const ps = pr * pc;
    return { perRow: pr, perCol: pc, perSheet: ps, sheets: Math.ceil(qty / ps) };
  }, [width, height, qty, margin, gap]);

  const handlePrint = () => {
    setPrinting(true);
    // Give React time to render all stickers, then fire the print dialog.
    // Chrome sometimes fires print before layout — a short RAF chain fixes it.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        setTimeout(() => {
          window.print();
          setPrinting(false);
        }, 150);
      })
    );
  };

  if (!product) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Dynamic @page size for roll printers (India / China labels only).
          When rotate is on, the page becomes portrait (H × W) and the sticker
          content is rotated 90° so it fills the physical label correctly.
          Chromium respects @page rules per print job. */}
      {isRoll && (
        <style>{`
          @media print {
            @page {
              size: ${rotate ? height : width}in ${rotate ? width : height}in;
              margin: 0;
            }
            .roll-sticker {
              width: ${rotate ? height : width}in !important;
              height: ${rotate ? width : height}in !important;
              page-break-after: always;
              break-after: page;
              margin: 0 !important;
              box-shadow: none !important;
              border: none !important;
              overflow: hidden;
              padding: 0 !important;
            }
            .roll-sticker:last-child {
              page-break-after: auto;
              break-after: auto;
            }
            .roll-sticker-inner {
              width: ${width}in;
              height: ${height}in;
              ${rotate
                ? `transform: rotate(90deg); transform-origin: top left; margin-left: ${height}in;`
                : ""}
            }
          }
        `}</style>
      )}

      {/* Toolbar */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-zinc-200 px-6 py-3 flex items-center gap-4 flex-wrap">
        <Button
          variant="ghost"
          className="h-9 px-2 -ml-2 hover:bg-zinc-100"
          onClick={() => navigate(`/edit/${id}`)}
          data-testid="print-back-btn"
        >
          <ArrowLeft size={16} />
        </Button>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">Print queue</div>
          <div className="text-[15px] font-semibold text-zinc-950 truncate">
            {product.product_name}
          </div>
        </div>

        <div className="flex items-center gap-3 ml-auto flex-wrap">
          <Field label="Width (in)">
            <Input
              type="number"
              step="0.1"
              min="1"
              max="8"
              value={width}
              onChange={(e) => setWidth(Math.max(0.5, Number(e.target.value) || 0.5))}
              className="h-9 w-20"
              data-testid="input-sticker-width"
            />
          </Field>
          <Field label="Height (in)">
            <Input
              type="number"
              step="0.1"
              min="1"
              max="8"
              value={height}
              onChange={(e) => setHeight(Math.max(0.5, Number(e.target.value) || 0.5))}
              className="h-9 w-20"
              data-testid="input-sticker-height"
            />
          </Field>
          <Field label="Quantity">
            <Input
              type="number"
              min="1"
              max={MAX_QTY}
              value={qty}
              onChange={(e) =>
                setQty(Math.min(MAX_QTY, Math.max(1, parseInt(e.target.value) || 1)))
              }
              className="h-9 w-24"
              data-testid="input-print-qty"
            />
          </Field>
          {!isRoll && (
            <>
              <Field label="Margin (in)">
                <Input
                  type="number"
                  step="0.05"
                  min="0"
                  max="1"
                  value={margin}
                  onChange={(e) => setMargin(Math.max(0, Number(e.target.value) || 0))}
                  className="h-9 w-20"
                  data-testid="input-margin"
                />
              </Field>
              <Field label="Gap (in)">
                <Input
                  type="number"
                  step="0.02"
                  min="0"
                  max="1"
                  value={gap}
                  onChange={(e) => setGap(Math.max(0, Number(e.target.value) || 0))}
                  className="h-9 w-20"
                  data-testid="input-gap"
                />
              </Field>
            </>
          )}

          {isRoll && (
            <Field label="Rotate 90°">
              <label className="inline-flex items-center h-9 gap-2 px-2 border border-zinc-200 rounded-md bg-white cursor-pointer" title="Rotate the sticker 90° for portrait-feed roll printers">
                <input
                  type="checkbox"
                  checked={rotate}
                  onChange={(e) => setRotate(e.target.checked)}
                  data-testid="toggle-rotate"
                  className="cursor-pointer"
                />
                <span className="text-[12px] text-zinc-700">{rotate ? "Portrait" : "Landscape"}</span>
              </label>
            </Field>
          )}

          <Button
            className="h-9 bg-zinc-950 text-white hover:bg-zinc-800 gap-2 ml-2"
            onClick={handlePrint}
            disabled={printing}
            data-testid="print-now-btn"
          >
            <Printer size={15} />
            {printing ? "Preparing…" : "Print / Save PDF"}
          </Button>
        </div>
      </div>

      {/* Info bar */}
      <div className="no-print px-6 py-3 bg-zinc-100 border-b border-zinc-200 text-[12px] text-zinc-600 flex items-center gap-4 flex-wrap">
        {isRoll ? (
          <>
            <div className="flex items-center gap-1.5" data-testid="print-mode-roll">
              <RulerDimensionLine size={14} />
              Roll format · <strong className="text-zinc-950">{rotate ? `${height}×${width}` : `${width}×${height}`} in</strong> per sticker
              {rotate && <span className="text-zinc-400 ml-1">(rotated 90°)</span>}
            </div>
            <div>
              → <strong className="text-zinc-950">{qty}</strong> sticker{qty !== 1 ? "s" : ""} (one per page)
            </div>
            <div className="text-zinc-400">
              Tip: In the browser print dialog set paper size to <strong>{rotate ? `${height}×${width}` : `${width}×${height}`} in</strong> (or &quot;Custom&quot;), scale 100%, and disable headers/footers.
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1.5" data-testid="print-mode-a4">
              <RulerDimensionLine size={14} />
              A4 (210×297mm) · {perRow}×{perCol} = <strong className="text-zinc-950">{perSheet}</strong> per sheet
            </div>
            <div>
              → <strong className="text-zinc-950">{sheets}</strong> sheet{sheets !== 1 ? "s" : ""} for {qty} label{qty !== 1 ? "s" : ""}
            </div>
            <div className="text-zinc-400">
              Tip: In the browser print dialog choose A4, scale 100%, and disable headers/footers.
            </div>
          </>
        )}
        {qty > 500 && (
          <div className="flex items-center gap-1.5 text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1">
            <AlertTriangle size={13} />
            Large batch — rendering may take a few seconds.
          </div>
        )}
      </div>

      {/* Print output */}
      {isRoll ? (
        <div className="print-container py-6 flex flex-col items-center gap-3" data-testid="print-container">
          {Array.from({ length: qty }).map((_, idx) => (
            <div
              key={idx}
              className="roll-sticker bg-white shadow-md"
              style={{
                width: `${rotate ? height : width}in`,
                height: `${rotate ? width : height}in`,
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              <div
                className="roll-sticker-inner"
                style={{
                  width: `${width}in`,
                  height: `${height}in`,
                  transform: rotate ? "rotate(90deg)" : "none",
                  transformOrigin: "top left",
                  marginLeft: rotate ? `${height}in` : 0,
                }}
              >
                <StickerRenderer
                  data={product}
                  widthIn={width}
                  heightIn={height}
                  barcodeDataUrl={barcodeDataUrl}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="print-container py-6 flex flex-col items-center gap-6" data-testid="print-container">
          {Array.from({ length: sheets }).map((_, sheetIdx) => {
            const start = sheetIdx * perSheet;
            const end = Math.min(start + perSheet, qty);
            return (
              <PrintSheet
                key={sheetIdx}
                margin={margin}
                gap={gap}
                width={width}
                height={height}
                perRow={perRow}
                count={end - start}
                product={product}
                barcodeDataUrl={barcodeDataUrl}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

const PrintSheet = React.memo(function PrintSheet({
  margin,
  gap,
  width,
  height,
  perRow,
  count,
  product,
  barcodeDataUrl,
}) {
  return (
    <div
      className="print-sheet bg-white shadow-md"
      style={{
        width: `${A4_IN.w}in`,
        height: `${A4_IN.h}in`,
        padding: `${margin}in`,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${perRow}, ${width}in)`,
          gridAutoRows: `${height}in`,
          gap: `${gap}in`,
          justifyContent: "start",
        }}
      >
        {Array.from({ length: count }).map((_, idx) => (
          <div
            key={idx}
            style={{
              width: `${width}in`,
              height: `${height}in`,
              contentVisibility: "auto",
              containIntrinsicSize: `${width}in ${height}in`,
            }}
          >
            <StickerRenderer
              data={product}
              sizeIn={width}
              widthIn={width}
              heightIn={height}
              barcodeDataUrl={barcodeDataUrl}
            />
          </div>
        ))}
      </div>
    </div>
  );
});

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">
      {label}
    </Label>
    {children}
  </div>
);
